import { useEffect, useState } from "react";
import { Volume2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { speak } from "@/lib/tts";
import { shuffle, randomFrom } from "@/lib/gameData";
import { GAME_FEEDBACK_MS } from "@/lib/constants";
import { VOCAB_ITEMS as VOCAB } from "@/lib/vocabData";
import { useAuth } from "@/context/AuthContext";

function newRound(current) {
  let pick = randomFrom(VOCAB);
  let guard = 5;
  while (current && pick.word === current.word && guard-- > 0) pick = randomFrom(VOCAB);
  const options = shuffle([pick.correct, ...pick.distractors]);
  return { item: pick, options };
}

export default function VocabInContext() {
  const [round, setRound] = useState(() => newRound(null));
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [picked, setPicked] = useState(null); // string or null
  const [locked, setLocked] = useState(false);
  const { award } = useAuth();

  useEffect(() => {
    setPicked(null);
    setLocked(false);
    const t = setTimeout(() => speak(round.item.sentence, { rate: 0.9 }), 250);
    return () => clearTimeout(t);
  }, [round]);

  const pick = (choice) => {
    if (locked) return;
    setLocked(true);
    setPicked(choice);
    setAttempts((a) => a + 1);
    if (choice === round.item.correct) {
      setScore((s) => s + 1);
      award(10, "vocab");
      toast.success(`Yes! "${round.item.word}" means "${choice}".`);
      speak(`${round.item.word}. ${choice}.`, { rate: 0.9 });
      setTimeout(() => setRound((cur) => newRound(cur.item)), GAME_FEEDBACK_MS + 400);
    } else {
      toast.error(`Not quite — the answer is "${round.item.correct}".`);
      setTimeout(() => setLocked(false), GAME_FEEDBACK_MS);
    }
  };

  const highlighted = round.item.sentence.split(new RegExp(`(${round.item.word})`, "i"));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div
          data-testid="vocab-score"
          className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 px-4 py-2 rounded-full font-bold text-sm"
        >
          Score: {score} / {attempts}
        </div>
        <button
          data-testid="vocab-next"
          onClick={() => setRound((cur) => newRound(cur.item))}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-sm text-slate-700 transition-colors"
        >
          <RefreshCcw className="w-4 h-4" /> New word
        </button>
      </div>

      <div className="bg-slate-50 rounded-3xl p-6 sm:p-8 mb-6">
        <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">
          Read the sentence &mdash; what does the bolded word mean?
        </p>
        <p className="text-xl sm:text-2xl leading-relaxed text-slate-800">
          {highlighted.map((part, i) =>
            part.toUpperCase() === round.item.word ? (
              <span key={`vocab-${round.item.word}-${i}`} className="font-display font-semibold text-indigo-600 underline decoration-2 underline-offset-4">
                {part}
              </span>
            ) : (
              <span key={`vocab-${round.item.word}-${i}`}>{part}</span>
            )
          )}
        </p>
        <button
          data-testid="vocab-play-sentence"
          onClick={() => speak(round.item.sentence, { rate: 0.9 })}
          className="mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border-2 border-slate-200 hover:border-indigo-400 text-slate-700 font-bold text-sm transition-colors"
        >
          <Volume2 className="w-4 h-4" /> Hear sentence
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {round.options.map((opt, i) => {
          const isCorrect = picked && opt === round.item.correct;
          const isWrongPick = picked === opt && opt !== round.item.correct;
          return (
            <button
              key={opt}
              data-testid={`vocab-option-${i}`}
              onClick={() => pick(opt)}
              disabled={locked}
              className={`text-left rounded-2xl border-2 p-4 font-bold text-lg transition-all
                ${isCorrect ? "bg-emerald-500 border-emerald-600 text-white phonx-letter-pop" : ""}
                ${isWrongPick ? "bg-rose-100 border-rose-300 text-rose-700 phonx-shake" : ""}
                ${!picked ? "bg-white border-slate-200 text-slate-800 hover:border-indigo-400 hover:text-indigo-600 shadow-sm hover:shadow-md" : ""}
              `}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}
